// Shared KPI slider primitives used by both the clinician follow-up dialog
// (admin_dashboard/.../ClinicianFollowupDialog.js) and the patient
// `Weekly Physio Check-in` flow (Assessment/questionaire_follow_up.js).
//
// Lifted out of ClinicianFollowupDialog.js so both surfaces use the same
// slider styling, kind resolution, and value clamping. Visual output is
// identical to the previous inline definitions.

import React from 'react';
import { Slider, Box, Typography, IconButton, TextField } from '@material-ui/core';
import { styled, makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';

// Slider styling mirrors the patient-facing assessment sliders in
// Assessment/question.js (PrettoSliderPain / PrettoSliderFunction) so the
// clinician sees the same gradient cues the patient would.
export const PainSlider = styled(Slider)(() => ({
  color: '#ffffff',
  height: 5,
  marginLeft: 8,
  width: '95%',
  '& .MuiSlider-thumb': {
    height: 22, width: 22, backgroundColor: '#fff',
    border: '2px solid #fb741a', marginTop: -10, marginLeft: -12,
    '&:focus, &:hover, &.Mui-active': { boxShadow: 'inherit' },
  },
  '& .MuiSlider-valueLabel': { left: 'calc(-25% + 0px)', color: '#1a90ff' },
  '& .MuiSlider-track': { height: 15, borderRadius: 4, opacity: 0.01 },
  '& .MuiSlider-rail': {
    height: 15, borderRadius: 10,
    background: 'linear-gradient(90deg, #17aa2d 25%, #FF0000 90%)',
    margin: -6,
  },
  '& .MuiSlider-markLabel': { fontWeight: 400, color: '#002D55' },
}));

export const FunctionSlider = styled(Slider)(() => ({
  color: '#ffffff',
  height: 5,
  margin: 8,
  paddingLeft: 6.5,
  width: '97%',
  '& .MuiSlider-thumb': {
    height: 22, width: 22, backgroundColor: '#fff',
    border: '2px solid #fb741a', marginTop: -10, marginLeft: -12,
    '&:focus, &:hover, &.Mui-active': { boxShadow: 'inherit' },
  },
  '& .MuiSlider-valueLabel': { left: 'calc(-25% + 0px)', color: '#1a90ff' },
  '& .MuiSlider-track': { height: 15, borderRadius: 4, opacity: 0.01 },
  '& .MuiSlider-rail': {
    height: 15, borderRadius: 10,
    background: 'linear-gradient(90deg, #FF0000 25%, #17aa2d 90%)',
    margin: -6,
  },
  '& .MuiSlider-markLabel': { fontWeight: 400, color: '#002D55' },
}));

export const FREQUENCY_MARKS = [
  { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' },
  { value: 4, label: '4' }, { value: 5, label: '5' }, { value: 6, label: '6' },
  { value: 7, label: '7' },
];

export const PAIN_MARKS = [
  { value: 0, label: '0' }, { value: 30, label: '30' }, { value: 50, label: '50' },
  { value: 70, label: '70' }, { value: 100, label: '100' },
];

export const FUNCTION_MARKS = [
  { value: 0, label: '0' }, { value: 30, label: '30' }, { value: 50, label: '50' },
  { value: 70, label: '70' }, { value: 100, label: '100' },
];

export const NPS_MARKS = Array.from({ length: 11 }).map((_, i) => ({ value: i, label: String(i) }));

/**
 * Determine the slider configuration kind for a given KPI item by inspecting
 * its kpi_type / kpi_subtype / question_identifier / question_type.
 *
 * `kind` values:
 *   - 'pain_current' | 'pain_atbest' | 'pain_atworst' — 0-100, pain gradient
 *   - 'frequency'   — 1-7, pain gradient (red = daily / worse)
 *   - 'function'    — 0-100, function gradient, with editable goal text
 *   - 'nps'         — 0-10
 *   - 'generic'     — 0-100 fallback
 */
export const resolveSliderKind = (item) => {
  const ks = (item.kpi_subtype || '').toLowerCase();
  const kt = (item.kpi_type || '').toLowerCase();
  const qid = (item.question_identifier || '').toLowerCase();
  const qt = (item.question_type || '').toLowerCase();

  if (ks === 'frequency' || qid.includes('frequency')) return 'frequency';
  if (ks === 'atworst' || qid.includes('atworst')) return 'pain_atworst';
  if (ks === 'atbest' || qid.includes('atbest')) return 'pain_atbest';
  if (ks === 'current' || qid.includes('current')) return 'pain_current';
  if (kt === 'function_goal' || ks === 'main_goal' || qid.includes('maingoal') || qid.includes('selfratedfunction')) return 'function';
  if (qt === 'selfratednps' || qid.includes('nps')) return 'nps';
  // Clinician-defined custom pain baselines (e.g. "pain with benchpress")
  // share the 0-100 pain scale but carry their own free-text title via
  // `item.label`. Route them to a dedicated `pain_custom` kind so the row
  // can render the patient-facing label instead of the generic
  // "Pain right now".
  if (kt === 'pain_baseline' && (ks === 'custom' || qid.includes('custom'))) return 'pain_custom';
  if (kt === 'pain_baseline' || qt === 'selfratedpain') return 'pain_current';
  return 'generic';
};

export const SLIDER_CONFIGS = {
  pain_current: {
    component: PainSlider, min: 0, max: 100, step: 1, marks: PAIN_MARKS,
    ends: ['no pain', 'worst pain imaginable'], unit: '/100', defaultValue: 0,
    title: 'Pain right now',
  },
  pain_custom: {
    component: PainSlider, min: 0, max: 100, step: 1, marks: PAIN_MARKS,
    ends: ['no pain', 'worst pain imaginable'], unit: '/100', defaultValue: 0,
    // Title is intentionally generic; KpiSliderRow overrides it with the
    // clinician-defined `item.label` (e.g. "Pain with benchpress").
    title: 'Custom pain metric',
  },
  pain_atbest: {
    component: PainSlider, min: 0, max: 100, step: 1, marks: PAIN_MARKS,
    ends: ['no pain', 'worst pain imaginable'], unit: '/100', defaultValue: 0,
    title: 'Pain at best',
  },
  pain_atworst: {
    component: PainSlider, min: 0, max: 100, step: 1, marks: PAIN_MARKS,
    ends: ['no pain', 'worst pain imaginable'], unit: '/100', defaultValue: 0,
    title: 'Pain at worst',
  },
  frequency: {
    component: PainSlider, min: 1, max: 7, step: 1, marks: FREQUENCY_MARKS,
    ends: ['once a week', 'daily'], unit: ' / 7 days', defaultValue: 1,
    title: 'Symptom frequency (days per week)',
  },
  function: {
    component: FunctionSlider, min: 0, max: 100, step: 1, marks: FUNCTION_MARKS,
    ends: ['unable', 'able as before problem'], unit: '/100', defaultValue: 50,
    title: 'Functional ability',
  },
  nps: {
    component: FunctionSlider, min: 0, max: 10, step: 1, marks: NPS_MARKS,
    ends: ['not at all', 'Would recommend'], unit: '/10', defaultValue: 5,
    title: 'Patient satisfaction (NPS)',
  },
  generic: {
    component: FunctionSlider, min: 0, max: 100, step: 1, marks: FUNCTION_MARKS,
    ends: ['low', 'high'], unit: '/100', defaultValue: 50,
    title: 'Score',
  },
};

export const clampToConfig = (val, cfg) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return cfg.defaultValue;
  const n = Number(val);
  if (n < cfg.min) return cfg.min;
  if (n > cfg.max) return cfg.max;
  return n;
};

/**
 * Normalize a raw kpi_items entry (from /treatment/treatments/<id>/kpi_items/
 * or /treatment/kpi_items/me/) into the shape the slider UI needs. Keeps the
 * canonical kpi_type/kpi_subtype fields so per-row slider config can be
 * resolved without re-parsing the identifier in three places.
 */
export const normalizeItem = (item) => {
  const history = Array.isArray(item.history) ? item.history : [];
  const baselineValue = history.length > 0 && history[0].value != null
    ? history[0].value
    : null;
  const latestValue = item.numeric_value != null
    ? item.numeric_value
    : (item.numerical_value != null ? item.numerical_value : null);
  return {
    id: item.id || null,
    question_identifier: item.question_identifier || item.mapped_key || item.id || '',
    question_type: item.question_type
      || (item.kpi_type === 'pain_baseline' ? 'selfRatedPain' : 'selfRatedFunction'),
    kpi_type: item.kpi_type || '',
    kpi_subtype: item.kpi_subtype || '',
    label: item.label || item.journalPhrase || item.question_identifier || '',
    numeric_value: latestValue,
    baseline_value: item.baseline_value != null
      ? item.baseline_value
      : (baselineValue != null ? baselineValue : latestValue),
    history,
  };
};

const useSliderRowStyles = makeStyles((theme) => ({
  kpiRow: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    border: '1px solid #e6eaf0',
  },
  // `large` variant: extra breathing room and fluid type for the patient
  // follow-up surface so the card scales with viewport width the same way
  // ChartProgress.js does. Uses clamp(min, vw, max) so type stays legible
  // on phones, scales naturally up to FHD, and tops out on 4K.
  kpiRowLarge: {
    padding: 'clamp(14px, 1.8vw, 28px)',
    borderRadius: 22,
    // xs: full width (matches the "I understand" start card)
    width: '92%',
    // sm and up: fluid clamp that widens through md/lg/xl
    [theme.breakpoints.up('xs')]: {
      width: 'clamp(260px, 70.4vw, 900px)',
    },
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: '#002D55',
    fontSize: 14,
  },
  labelLarge: {
    // Reduced ceiling on lg/xl: was clamp(16,1.5vw,26)
    fontSize: 'clamp(15px, 1.25vw, 20px)',
    marginBottom: theme.spacing(0.75),
  },
  subLabel: {
    color: '#64748B',
    fontSize: 11,
    marginBottom: theme.spacing(1),
  },
  subLabelLarge: {
    fontSize: 'clamp(11px, 0.9vw, 15px)',
  },
  valueDisplay: {
    fontWeight: 700,
    color: '#002D55',
    fontSize: 22,
    minWidth: 56,
    textAlign: 'right',
  },
  // Reduced ceiling: was clamp(26,2.4vw,44)
  valueDisplayLarge: {
    fontSize: 'clamp(22px, 1.9vw, 34px)',
    minWidth: 'clamp(60px, 5vw, 90px)',
  },
  unitDisplay: {
    color: '#64748B',
    fontSize: 12,
    marginLeft: 4,
  },
  unitDisplayLarge: {
    fontSize: 'clamp(11px, 0.85vw, 15px)',
    marginLeft: 5,
  },
  baselineHint: {
    color: '#64748B',
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 2,
  },
  baselineHintLarge: {
    fontSize: 'clamp(11px, 0.85vw, 14px)',
    marginTop: 3,
  },
  baselineValue: {
    fontWeight: 700,
    fontStyle: 'normal',
    color: '#002D55',
    fontSize: 12,
  },
  baselineValueLarge: {
    fontSize: 'clamp(12px, 0.9vw, 15px)',
  },
  kpi_pretitle: {
    fontSize: 'clamp(11px, 0.85vw, 14px)',
    marginTop: -6,
  },
  scaleEnds: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#64748B',
    fontSize: 11,
    marginTop: 4,
    marginBottom: 2,
  },
  scaleEndsLarge: {
    fontSize: 'clamp(11px, 0.9vw, 14px)',
    marginTop: 6,
    marginBottom: 3,
  },
  goalText: {
    fontWeight: 600,
    color: '#002D55',
    fontSize: 13,
  },
  goalTextLarge: {
    fontSize: 'clamp(13px, 1.1vw, 18px)',
  },
  goalEdit: {
    marginTop: 4,
    width: '100%',
  },
  // Patient-size sliders: fully fluid thumb and rail so everything scales
  // uniformly from xs to xl without fixed-px overrides.
  sliderLarge: {
    '& .MuiSlider-thumb': {
      // was 30px fixed — now fluid, smaller ceiling on lg/xl
      height: 'clamp(20px, 1.8vw, 26px)',
      width: 'clamp(20px, 1.8vw, 26px)',
      marginTop: 'clamp(-12px, -1.1vw, -9px)',
      marginLeft: 'clamp(-13px, -1.15vw, -10px)',
      borderWidth: 3,
    },
    '& .MuiSlider-rail, & .MuiSlider-track': {
      // was 19px fixed — fluid, reduced on lg/xl
      height: 'clamp(14px, 1.3vw, 18px)',
      borderRadius: 12,
    },
    '& .MuiSlider-markLabel': {
      fontSize: 'clamp(11px, 0.85vw, 14px)',
      paddingTop: 2,
    },
  },
}));

/**
 * KpiSliderRow — presentational row for one KPI: title, optional editable
 * goal text (function items), baseline/latest hint, value readout, and the
 * gradient slider. Visual output mirrors the previous inline `renderItem`
 * in ClinicianFollowupDialog.js.
 *
 * Props:
 *   item              – normalized KPI item (see normalizeItem)
 *   value             – current slider value
 *   onChange(qid,val) – slider change handler
 *   labelText         – current goal text (function items)
 *   onLabelChange(qid,val) – goal text change handler
 *   isEditingLabel    – bool, function items only
 *   onLabelEditToggle(qid,bool) – toggle goal-edit mode
 *   allowGoalEdit     – bool (default true); hides the pencil when false
 *   showBaseline      – bool (default true); hides Baseline/Latest line when false
 */
export const KpiSliderRow = ({
  item,
  value,
  onChange,
  labelText,
  onLabelChange,
  isEditingLabel,
  onLabelEditToggle,
  allowGoalEdit = true,
  showBaseline = true,
  size = 'default',
}) => {
  const classes = useSliderRowStyles();
  const isLarge = size === 'large';
  const cn = (base, large) => (isLarge ? `${classes[base]} ${classes[large]}` : classes[base]);
  const kind = resolveSliderKind(item);
  const cfg = SLIDER_CONFIGS[kind];
  const SliderComp = cfg.component;
  const qid = item.question_identifier;
  const currentValue = value != null ? value : cfg.defaultValue;
  const isFunction = kind === 'function';
  // For clinician-defined custom pain baselines the meaningful title is the
  // KpiMeasurement.label (e.g. "Pain with benchpress"), not the generic
  // SLIDER_CONFIGS.pain_custom.title. Strip the boilerplate "Patient
  // reported:" / "Patient reported pain " prefixes and capitalize so the
  // header reads like a normal metric name.
  const renderTitle = (() => {
    if (kind !== 'pain_custom') return cfg.title;
    const raw = String(item.label || labelText || '').trim();
    if (!raw) return cfg.title;
    let cleaned = raw
      .replace(/^patient reported( pain)?\s*[:\u2014\u2013\-]?\s*/i, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned) return cfg.title;
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  })();
  const baselineValue = item.baseline_value != null ? item.baseline_value : item.numeric_value;
  const latestValue = item.numeric_value;
  const showLatest = latestValue != null
    && baselineValue != null
    && Number(latestValue) !== Number(baselineValue);
  const renderMetric = (label, val) => (
    <>
      {label}: <span className={cn('baselineValue', 'baselineValueLarge')}>{val}</span>{cfg.unit}
    </>
  );
  const baselineNode = showBaseline && baselineValue != null ? renderMetric('Baseline', baselineValue) : null;
  const latestNode = showBaseline && showLatest ? renderMetric('Latest', latestValue) : null;

  return (
    <Box className={cn('kpiRow', 'kpiRowLarge')}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box flex={1} minWidth={0} pr={2}>
           <Typography className={cn('kpi_pretitle')}>Please rate your:</Typography>
          <Typography className={cn('label', 'labelLarge')}>{renderTitle}</Typography>
          {isFunction ? (
            isEditingLabel && allowGoalEdit ? (
              <TextField
                className={classes.goalEdit}
                value={labelText || ''}
                onChange={(e) => onLabelChange && onLabelChange(qid, e.target.value)}
                onBlur={() => onLabelEditToggle && onLabelEditToggle(qid, false)}
                variant="outlined"
                size="small"
                autoFocus
                placeholder="Describe the activity"
              />
            ) : (
              <Box display="flex" alignItems="center">
                <Typography className={cn('goalText', 'goalTextLarge')}>
                  "{labelText || item.label || 'Functional goal'}"
                </Typography>
                {allowGoalEdit && (
                  <IconButton
                    size="small"
                    onClick={() => onLabelEditToggle && onLabelEditToggle(qid, true)}
                    aria-label="edit-goal-text"
                  >
                    <EditIcon style={{ fontSize: isLarge ? 22 : 16, color: '#002D55' }} />
                  </IconButton>
                )}
              </Box>
            )
          ) : null}
          {(baselineNode || latestNode) && (
            <Typography className={cn('baselineHint', 'baselineHintLarge')}>
              {baselineNode}
              {baselineNode && latestNode ? (
                <span style={{ margin: '0 14px', color: '#cbd5e1' }}>|</span>
              ) : null}
              {latestNode}
            </Typography>
          )}
        </Box>
        <Typography className={cn('valueDisplay', 'valueDisplayLarge')}>
          {currentValue}
          <span className={cn('unitDisplay', 'unitDisplayLarge')}>{cfg.unit}</span>
        </Typography>
      </Box>
      <Box className={cn('scaleEnds', 'scaleEndsLarge')}>
        <span>{cfg.ends[0]}</span>
        <span>{cfg.ends[1]}</span>
      </Box>
      <SliderComp
        className={isLarge ? classes.sliderLarge : undefined}
        value={currentValue}
        min={cfg.min}
        max={cfg.max}
        step={cfg.step}
        marks={cfg.marks}
        valueLabelDisplay="auto"
        onChange={(_, val) => onChange && onChange(qid, val)}
      />
    </Box>
  );
};
